// src/components/imports/ImportProgressChip.tsx
//
// Sticky progress chip that lives just under the header while a
// FileMaker import/preview/rollback job is running. Lets the user
// browse the rest of the site without losing the running analysis.
// Hidden for anonymous users (the endpoint refuses to serve them
// anyway, but we also don't render for them so the layout is
// always clean).

import { useEffect, useState } from "react";
import { Loader2, X, Database, CheckCircle2, XCircle } from "lucide-react";
import { useImportJobs, type ImportJob } from "@/lib/importJobs";
import { useAuth } from "@/components/auth/AuthContext";
import { cn } from "@/lib/cn";

const KIND_LABEL: Record<string, string> = {
  preview: "FM preview",
  apply: "FM import",
  rollback: "FM rollback",
};

export function ImportProgressChip() {
  const auth = useAuth();
  const { jobs, refresh, cancel } = useImportJobs(
    auth.isAuthenticated && auth.accountType !== "anonymous",
  );
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // When a job transitions to done/error, auto-hide it after 4s
  useEffect(() => {
    const just_done = jobs.filter(
      (j) => j.phase !== "running" && !dismissed.has(j.job_id),
    );
    if (just_done.length === 0) return;
    const t = setTimeout(() => {
      setDismissed((s) => {
        const n = new Set(s);
        just_done.forEach((j) => n.add(j.job_id));
        return n;
      });
    }, 4_000);
    return () => clearTimeout(t);
  }, [jobs, dismissed]);

  if (!auth.isAuthenticated) return null;

  const visible = jobs.filter((j) => !dismissed.has(j.job_id));
  if (visible.length === 0) return null;

  const first: ImportJob = visible[0]!;
  const extra = visible.length - 1;
  const color = first.phase === "error"
    ? "text-accent-rose" : "text-accent-indigo";
  const barColor = first.phase === "error"
    ? "bg-accent-rose" : first.phase === "done"
      ? "bg-accent-green" : "bg-accent-indigo";
  const Icon = first.phase === "error" ? XCircle
             : first.phase === "done" ? CheckCircle2
             : Loader2;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "sticky top-16 z-30 w-full border-b border-border bg-bg-elevated/95",
        "backdrop-blur-md",
      )}
    >
      <div className="container flex items-center gap-3 py-2">
        <Icon
          className={cn("h-4 w-4 shrink-0", color,
                        first.phase === "running" && "animate-spin")}
          strokeWidth={1.5}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-510 text-fg-primary">
              {KIND_LABEL[first.kind] ?? first.kind}
              {first.batch_id ? ` · ${first.batch_id}` : ""}
            </span>
            <span className="text-[10px] text-fg-tertiary">
              {first.message}
            </span>
            {extra > 0 && (
              <span className="ml-auto text-[10px] text-fg-tertiary">
                +{extra} more
              </span>
            )}
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-bg-canvas/60">
            <div
              className={cn("h-full transition-all duration-300", barColor)}
              style={{ width: `${Math.max(2, first.progress)}%` }}
            />
          </div>
        </div>
        <span className={cn("shrink-0 font-mono text-xs tabular-nums", color)}>
          {first.progress}%
        </span>
        {first.phase === "running" && (
          <button
            type="button"
            onClick={() => void cancel(first.job_id).then(refresh)}
            title="Cancel"
            className="shrink-0 text-fg-tertiary hover:text-fg-primary"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {first.phase !== "running" && (
          <button
            type="button"
            onClick={() => setDismissed((s) => {
              const n = new Set(s); n.add(first.job_id); return n;
            })}
            title="Dismiss"
            className="shrink-0 text-fg-tertiary hover:text-fg-primary"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <Database className="hidden h-3.5 w-3.5 shrink-0 text-fg-quaternary md:inline" />
      </div>
    </div>
  );
}
